import type { StaffSSOProvider, StaffSSOProviderRequest } from '@central/api';
import { Card, StatusChip } from '@central/ui';
import { Button, Table, VisuallyHidden } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { keys, useApi } from '../api';
import { mono, muted } from '../lib/labels';
import { useMe } from '../lib/me';
import { DataTable, Empty, QueryState } from './parts';
import { ssoKindLabel } from '../lib/sso';
import { SsoProviderEditor, type EditableProvider } from './SsoProviderEditor';

/**
 * The identity providers console users sign in through, of one owner:
 * Rethink's (resellerId "") or one reseller's. Owners add, edit and remove
 * them; Admins read. A Rethink provider is offered on the sign-in page and
 * signs in Rethink staff only; a reseller's is found by email domain and
 * signs in only that reseller's engineers. Either way the user must already
 * have a console account.
 */
export function StaffSsoProviders({
  resellerId,
  title = 'Sign-in (SSO)',
}: {
  resellerId: string;
  title?: string;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const canWrite = useMe().user.role === 'Owner';
  const providers = useQuery({
    queryKey: keys.staffSso(resellerId),
    queryFn: () => api.staffSso.list(resellerId),
  });
  // undefined: closed; null: a new provider; a provider: that one.
  const [editing, setEditing] = useState<EditableProvider | null>();
  const items = providers.data?.items ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.staffSsoAll });
  const rethink = resellerId === '';

  return (
    <Card
      title={title}
      padded={false}
      action={
        canWrite && (
          <Button size="xs" color="dark" onClick={() => setEditing(null)}>
            Add provider
          </Button>
        )
      }
    >
      <QueryState isPending={providers.isPending} error={providers.error}>
        {() =>
          items.length === 0 ? (
            <Empty>
              {rethink
                ? 'No identity provider. Staff sign in with a password and second factor.'
                : 'No identity provider. This reseller’s engineers sign in with a password and second factor.'}
            </Empty>
          ) : (
            <DataTable
              label={rethink ? 'Staff identity providers' : 'Reseller identity providers'}
              head={['Name', 'Type', 'Email domains', 'Status', '']}
            >
              {items.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td style={{ fontWeight: 600 }}>{p.name}</Table.Td>
                  <Table.Td>{ssoKindLabel[p.kind]}</Table.Td>
                  <Table.Td style={mono}>{p.domains.join(', ')}</Table.Td>
                  <Table.Td>
                    <StatusChip tone={p.enabled ? 'ok' : 'unk'}>
                      {p.enabled ? 'Enabled' : 'Disabled'}
                    </StatusChip>
                  </Table.Td>
                  <Table.Td>
                    <Button variant="subtle" size="xs" onClick={() => setEditing(p)}>
                      {canWrite ? 'Edit' : 'View'} <VisuallyHidden>{p.name}</VisuallyHidden>
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </DataTable>
          )
        }
      </QueryState>
      <p style={{ margin: 0, padding: '12px 20px', fontSize: 13, ...muted }}>
        {rethink
          ? 'Enabled providers appear on the sign-in page as “Continue with …” and sign in Owners, Admins and Support only. Invite people on Staff first: a first sign-in uses up their setup link, or links to their existing account after their password and second factor.'
          : 'Engineers find this provider with “Use my organization’s SSO” and their work email. It signs in only this reseller’s engineers, who must be invited on Staff first.'}
      </p>
      {editing !== undefined && (
        <SsoProviderEditor
          provider={editing}
          readOnly={!canWrite}
          metadataUrl
          idpOwner={
            rethink ? 'Rethink’s identity provider (Entra ID)' : 'the reseller’s identity provider'
          }
          issuerHint={
            rethink
              ? 'https://login.microsoftonline.com/<tenant-id>/v2.0'
              : 'https://example.okta.com'
          }
          save={async (body, id) => {
            const b = body as StaffSSOProviderRequest;
            const p: StaffSSOProvider = id
              ? await api.staffSso.update(id, b)
              : await api.staffSso.create({ ...b, resellerId: rethink ? undefined : resellerId });
            await refresh();
            return p;
          }}
          remove={async (id) => {
            await api.staffSso.delete(id);
            await refresh();
          }}
          onSaved={setEditing}
          onClose={() => setEditing(undefined)}
        />
      )}
    </Card>
  );
}
